import { PermissionGate } from "@components/PermissionGate";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { AiOutlineFileImage, AiOutlineFilePdf } from "react-icons/ai";
import { LiaFileUploadSolid } from "react-icons/lia";
import {
  ActionIcon,
  Alert,
  Button,
  ComboboxItem,
  Group,
  Loader,
  Modal,
  ScrollAreaAutosize,
  Select,
  Tabs,
  Text,
  Tooltip,
} from "@mantine/core";
import { IconInfoCircle, IconTrash } from "@tabler/icons-react";

import { openDeleteModal } from "@components/DeleteModal";
import { ScrollArea } from "@components/ScrollArea";

import { handleOnChange } from "@utils/handleOnChange";
import { truncateWithEllipsis } from "@utils/truncateText";

import { getLabsStatuses } from "@data/dropdownValues";

import { documentFileTypes, imageFileTypes } from "@constants/fileTypes";
import {
  MAX_FILENAME_LENGTH_AFTER_UPLOAD,
  MAX_FILENAME_LENGTH_BEFORE_UPLOAD,
  MEGABYTE,
  ROLES,
} from "@constants/index";

import { useDeleteLabFile } from "@hooks/mutations/lab/useDeleteLabFile";
import { useUpdateLab } from "@hooks/mutations/lab/useUpdateLab";
import { useGetUsers } from "@hooks/queries/user/useGetUsers";

import { useAuth } from "@contexts/auth";

import { Laboratory } from "@customTypes/laboratory";

import { StyledEditModalContent } from "./EditModal.styled";


interface EditModalProps {
  labTypes: ComboboxItem[];
  lab: Partial<Laboratory>;
  opened: boolean;
  close: () => void;
}

export const EditModal = ({
  labTypes,
  lab: initialLab,
  opened,
  close,
}: EditModalProps) => {
  const { t } = useTranslation();
  const { token, roles , can } = useAuth();

  const [uploadedFiles, setUploadedFiles] = useState<File[] | []>([]);
  const [lab, setLab] = useState<Partial<Laboratory>>(initialLab);
  const [labType, setLabType] = useState<ComboboxItem | null>({
    label: lab?.labType ?? "",
    value: lab?.labTypeId ?? "",
  });
  const [status, setStatus] = useState<string | null>(null);
  const [formData, setFormData] = useState(lab ?? {});
  const [fileIndex, setFileIndex] = useState(-1);
  const [labFiles, setLabFiles] = useState<string[]>(lab?.files as string[]);
  const [doctor, setDoctor] = useState<ComboboxItem | null>(null);
  const [supportStaff, setSupportStaff] = useState<ComboboxItem | null>(null);
  const [modifiedFields, setModifiedFields] = useState<any>({});

  const { mutate: updateLab, isLoading: isUpdateLabLoading } = useUpdateLab();
  const { mutate: deleteLabFile, isLoading: isDeleteLabFileLoading } =
    useDeleteLabFile();
  const {
    data: doctorsData,
    isLoading: isDoctorsLoading,
    isRefetching: isDoctorsRefetching,
  } = useGetUsers({ permission: "labs.create" });
  const {
    data: supportStaffData,
    isLoading: isSupportStaffLoading,
    isRefetching: isSupportStaffRefetching,
  } = useGetUsers({ permission: "labs.edit" });

  const doctorsComboboxItems = useMemo(() => {
    return doctorsData?.map(item => ({
      label: item.fullName,
      value: item.id,
    }));
  }, [doctorsData]);
  const supportStaffComboboxItems = useMemo(() => {
    return supportStaffData?.map(item => ({
      label: item.fullName,
      value: item.id,
    }));
  }, [supportStaffData]);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      setUploadedFiles([...uploadedFiles, ...acceptedFiles]);
    },
    [uploadedFiles]
  );

  const {
    getRootProps,
    getInputProps,
    isDragAccept,
    isDragActive,
    isDragReject,
  } = useDropzone({
    accept: {
      "image/*": imageFileTypes,
      "application/pdf": documentFileTypes,
    },
    onDrop,
  });

  const removeFile = (file: File) => {
    const newFiles = [...uploadedFiles];
    newFiles.splice(newFiles.indexOf(file), 1);
    setUploadedFiles(newFiles);
  };

  const handleUpdateLab = () => {
    //* Using built-in FormData object to help submit files
    //* The regular "formData" I've been using does not work with files
    //* Check useCreateLab mutation for more info
    const jsFormData = new FormData();

    if (modifiedFields?.files) {
      modifiedFields.files.forEach((file: File) => {
        jsFormData.append(`files`, file);
      });
    }
    if (modifiedFields?.doctor) {
      jsFormData.append("ordered_by", modifiedFields.doctor);
    }
    if (modifiedFields?.supportStaff) {
      jsFormData.append("aid", modifiedFields.supportStaff);
    }
    if (modifiedFields?.labType) {
      jsFormData.append("lab_type", modifiedFields.labType);
    }
    if (modifiedFields?.status) {
      jsFormData.append("status", modifiedFields.status);
    }
    if (modifiedFields?.reason) {
      jsFormData.append("reason", modifiedFields.reason);
    }

    console.group("jsFormData");
    jsFormData.forEach((value, key) => {
      console.log(`${key}: ${value}`);
    });
    console.groupEnd();

    updateLab(
      {
        lab: jsFormData,
        labId: formData.id as string,
        patientId: lab.patientId as string,
      },
      {
        onSuccess: () => {
          setUploadedFiles([]);
          close();
        },
      }
    );
  };

  useEffect(() => {
    if (initialLab) {
      setLab(initialLab);
      setFormData({ ...initialLab });
      setDoctor({
        label: initialLab.orderedBy?.fullName as string,
        value: initialLab.orderedBy?.id as string,
      });
      setSupportStaff({
        label: initialLab.supportStaff?.fullName as string,
        value: initialLab.supportStaff?.id as string,
      });
      setStatus(initialLab?.status as string);
      setLabType({
        label: initialLab.labType as string,
        value: initialLab.labTypeId as string,
      });
      setLabFiles(initialLab?.files as string[]);
    }
  }, [initialLab]);

  useEffect(() => {
    // todo: Find a way to generalize this effect as a reusable hook
    if (!lab?.id) return;
    // Track fields that have been modified and update only those
    const modifications: any = {};

    if (
      (formData?.reason?.trim() ?? "") !== (initialLab?.reason?.trim() ?? "")
    ) {
      import.meta.env.DEV && console.log(formData?.reason, initialLab?.reason);
      modifications.reason = formData?.reason;
    } else delete modifications.reason;
    if (
      doctor?.value !== undefined &&
      doctor?.value !== initialLab?.orderedBy?.id
    ) {
      import.meta.env.DEV &&
        console.log(doctor?.value, initialLab?.orderedBy?.id);
      modifications.doctor = doctor?.value;
    } else delete modifications.doctor;
    if (
      supportStaff?.value !== undefined &&
      supportStaff?.value !== initialLab?.supportStaff?.id
    ) {
      import.meta.env.DEV &&
        console.log(supportStaff?.value, initialLab?.supportStaff?.id);
      modifications.supportStaff = supportStaff?.value;
    } else delete modifications.supportStaff;
    if (status !== null && status !== initialLab?.status) {
      import.meta.env.DEV && console.log(status, initialLab?.status);
      modifications.status = status;
    } else delete modifications.status;
    if (
      labType?.value !== undefined &&
      labType?.value !== initialLab?.labTypeId
    ) {
      import.meta.env.DEV && console.log(labType?.value, initialLab?.labTypeId);
      modifications.labType = labType?.value;
    } else delete modifications.labType;
    if (uploadedFiles.length > 0) {
      import.meta.env.DEV && console.log(uploadedFiles.length);
      modifications.files = uploadedFiles;
    } else delete modifications.files;

    setModifiedFields(modifications);
  }, [
    lab,
    formData?.reason,
    doctor?.value,
    supportStaff?.value,
    status,
    labType?.value,
    uploadedFiles,
  ]);

  // useEffect(() => {
  //   console.log("modifiedFields", modifiedFields);
  // }, [modifiedFields]);

  return (
    <Modal
      opened={opened}
      onClose={close}
      centered
      title={<Text fw={500}>{t("edit-lab")}</Text>}
      scrollAreaComponent={ScrollAreaAutosize}>
      <StyledEditModalContent>
        <Tabs defaultValue="info">
          <Tabs.List grow>
            <Tabs.Tab value="info">{t("info")}</Tabs.Tab>
            <Tabs.Tab value="previous-files">{t("previous-files")}</Tabs.Tab>
            <Tabs.Tab value="add-files">{t("add-files")}</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="info">
            <form className="form">
              <div className="form-field">
                <Select
                  label={t("lab-type")}
                  description={
                    formData.billStatus === "paid" &&
                    t("lab-paid-cannot-modify")
                  }
                  placeholder={t("select-lab-type")}
                  data={labTypes}
                  value={labType?.value ?? null}
                  onChange={(_val, option) => setLabType(option)}
                  withAsterisk
                  required
                  searchable
                  nothingFoundMessage={t("nothing-found")}
                  comboboxProps={{ withinPortal: false }}
                  disabled={formData.billStatus === "paid"}
                />
              </div>

              <div className="form-field">
                <Select
                  label={t("doctor")}
                  placeholder={t("select-doctor")}
                  withAsterisk
                  disabled={!can("labs.assign")}
                  data={doctorsComboboxItems || []}
                  value={doctor?.value ?? null}
                  onChange={(_val, option) => setDoctor(option)}
                  searchable
                  rightSection={
                    isDoctorsLoading || isDoctorsRefetching ? (
                      <Loader size={16} color="accent.4" />
                    ) : null
                  }
                />
              </div>

              <div className="form-field">
                <Select
                  label={t("support-staff")}
                  placeholder={t("select-support-staff")}
                  data={supportStaffComboboxItems || []}
                  value={supportStaff?.value ?? null}
                  onChange={(_val, option) => setSupportStaff(option)}
                  rightSection={
                    isSupportStaffLoading || isSupportStaffRefetching ? (
                      <Loader size={16} color="accent.4" />
                    ) : null
                  }
                  searchable
                />
              </div>

              <div className="form-field">
                <Select
                  withAsterisk
                  label={t("status")}
                  placeholder={t("select-status")}
                  allowDeselect={false}
                  data={getLabsStatuses()}
                  value={status}
                  onChange={setStatus}
                />
              </div>

              <div className="form-field">
                <label htmlFor="reason">{t("reason")}</label>
                <textarea
                  rows={5}
                  placeholder={t("type-reason")}
                  id="reason"
                  name="reason"
                  value={formData.reason ?? ""}
                  onChange={e =>
                    handleOnChange(e, setFormData, formData)
                  }></textarea>
              </div>
            </form>
          </Tabs.Panel>

          <Tabs.Panel value="previous-files" className="previous-files">
            <div className="files-wrapper">
              <div className="uploaded-files">
                <h4>{t("previously-uploaded-files")}</h4>
                <ScrollArea
                  scrollbarOrientation="vertical"
                  className={`previous-files ${
                    lab?.files?.length && lab.files.length > 6
                      ? "show-gradient"
                      : ""
                  }`}>
                  {labFiles?.length ? (
                    <ul>
                      {labFiles.reverse().map((file, index) => (
                        <li key={index}>
                          <div className="content">
                            {(file as string).slice(-3) === "pdf" ? (
                              <AiOutlineFilePdf />
                            ) : (
                              <AiOutlineFileImage />
                            )}
                            <a
                              target="_blank"
                              rel="noreferrer"
                              href={"https://" + file + `?token=${token}`}>
                              {truncateWithEllipsis(
                                (file as string).split("/")[3],
                                MAX_FILENAME_LENGTH_AFTER_UPLOAD
                              )}
                            </a>
                          </div>

                          <Tooltip label={t("delete-lab-file")} withArrow>
                            <PermissionGate permission="labs.delete"><ActionIcon
                              color="red"
                              loading={
                                isDeleteLabFileLoading && fileIndex === index
                              }
                              variant="outline"
                              onClick={() => {
                                setFileIndex(index);
                                openDeleteModal({
                                  textNode: (
                                    <Text fz={"sm"}>
                                      {t("delete-lab-file-message")}
                                    </Text>
                                  ),
                                  onConfirm: () => {
                                    if (lab?.id) {
                                      deleteLabFile(
                                        {
                                          labId: lab.id,
                                          fileName: (file as string).split(
                                            "/"
                                          )[3],
                                          patientId: lab.patientId as string,
                                        },
                                        {
                                          onSuccess: () =>
                                            setLabFiles(
                                              labFiles.filter(
                                                (_, idx) => idx !== index
                                              )
                                            ),
                                        }
                                      );
                                    }
                                  },
                                });
                              }}>
                              <IconTrash size={16} />
                            </ActionIcon></PermissionGate>
                          </Tooltip>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="empty-label">
                      {t("empty-previously-uploaded-files")}
                    </p>
                  )}
                </ScrollArea>
              </div>
            </div>
          </Tabs.Panel>

          <Tabs.Panel value="add-files">
            <div className="files-wrapper">
              <div
                {...getRootProps({
                  className: `dropzone ${isDragActive ? "active" : ""} ${
                    isDragAccept ? "accept" : ""
                  } ${isDragReject ? "reject" : ""}`,
                })}>
                <input {...getInputProps()} />

                <div className="text-content">
                  <div className="svg-wrapper">
                    <LiaFileUploadSolid />
                  </div>
                  <div className="p-wrapper">
                    <p>{t("drag-file-here")}</p>
                    <p>{t("drag-file-here-continuation")}</p>
                  </div>
                </div>
              </div>
              <div className="uploaded-files">
                <h4>{t("uploaded-files")}</h4>
                {!uploadedFiles.length ? (
                  <Alert
                    mt={"md"}
                    variant="light"
                    color="blue"
                    icon={<IconInfoCircle />}>
                    <Text fz={14} lh={1.5} c="blue">
                      {t("files-appear-info")}
                    </Text>
                  </Alert>
                ) : (
                  <ScrollArea
                    scrollbarOrientation="vertical"
                    className={`uploaded-files ${
                      uploadedFiles.length > 2 ? "show-gradient" : ""
                    }`}>
                    <ul>
                      {uploadedFiles.map(file => (
                        <li key={file.name}>
                          <div className="content">
                            {file.type === "application/pdf" ? (
                              <AiOutlineFilePdf />
                            ) : (
                              <AiOutlineFileImage />
                            )}
                            <p>
                              {truncateWithEllipsis(
                                file.name,
                                MAX_FILENAME_LENGTH_BEFORE_UPLOAD
                              )}{" "}
                              ({(file.size / MEGABYTE).toFixed(2)} {t("mb")})
                            </p>
                          </div>

                          <ActionIcon
                            variant="outline"
                            color="red"
                            onClick={() => removeFile(file)}>
                            <IconTrash size={16} />
                          </ActionIcon>
                        </li>
                      ))}
                    </ul>
                  </ScrollArea>
                )}
              </div>
            </div>
          </Tabs.Panel>
        </Tabs>

        <Group justify="end" mt="xl" pb="md">
          <PermissionGate permission="labs.edit"><Button
            onClick={handleUpdateLab}
            loading={isUpdateLabLoading}
            disabled={Object.keys(modifiedFields).length === 0}>
            {t("update-lab")}
          </Button></PermissionGate>
        </Group>
      </StyledEditModalContent>
    </Modal>
  );
};
